// frontend/src/components/Settings/NotificationSettings.jsx

import React, { useState, useEffect } from 'react';
import { notificationsAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import { useToast } from '../../hooks/useToast';
import Toast from '../Common/Toast';
import * as FiIcons from 'react-icons/fi';
import './NotificationSettings.css';

// ─── FilterRow: shared for add and edit ──────────────────────────────────────
const FilterRow = ({ entityType, initial, onSave, onCancel }) => {
  const { t } = useTranslation();
  const [fields, setFields] = useState([]);
  const [fieldName,        setFieldName]        = useState(initial?.field_name        || '');
  const [operator,         setOperator]         = useState(initial?.operator            || 'eq');
  const [fieldValue,       setFieldValue]       = useState(initial?.field_value        || '');
  const [logicalOperator,  setLogicalOperator]  = useState(initial?.logical_operator   || 'AND');
  const [selectedField,    setSelectedField]    = useState(null);

  const OPERATORS = [
    { value: 'eq',       label: t('notification.filter.eq',       'equals') },
    { value: 'neq',      label: t('notification.filter.neq',      'not equals') },
    { value: 'in',       label: t('notification.filter.in',       'is one of') },
    { value: 'nin',      label: t('notification.filter.nin',      'is not one of') },
    { value: 'contains', label: t('notification.filter.contains', 'contains') },
    { value: 'gt',       label: t('notification.filter.gt',       'greater than') },
    { value: 'lt',       label: t('notification.filter.lt',       'less than') },
  ];

  useEffect(() => {
    if (!entityType) return;
    notificationsAPI.getFilterableFields(entityType)
      .then(r => {
        if (r.data.success) {
          setFields(r.data.data);
          if (fieldName) setSelectedField(r.data.data.find(f => f.field === fieldName) || null);
        }
      }).catch(() => {});
  }, [entityType]);


  const handleFieldChange = (f) => {
    setFieldName(f);
    setFieldValue('');
    setSelectedField(fields.find(x => x.field === f) || null);
  };

  const isMulti = operator === 'in' || operator === 'nin';
  const canSave = fieldName && fieldValue.trim();

  return (
    <div className="filter-row-edit">
      <select value={fieldName} onChange={e => handleFieldChange(e.target.value)} className="fr-select fr-field">
        <option value="">Field…</option>
        {fields.map(f => <option key={f.field} value={f.field}>{f.label}</option>)}
      </select>
      <select value={operator} onChange={e => setOperator(e.target.value)} className="fr-select fr-op">
        {OPERATORS.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
      </select>
      {selectedField?.type === 'lookup' && selectedField?.lookup_values?.length && !isMulti ? (
        <select value={fieldValue} onChange={e => setFieldValue(e.target.value)} className="fr-select fr-val">
          <option value="">Select…</option>
          {selectedField.lookup_values.map(v => (
            <option key={v.value} value={v.value}>{v.label} ({v.value})</option>
          ))}
        </select>
      ) : selectedField?.type === 'select' && !isMulti ? (
        <select value={fieldValue} onChange={e => setFieldValue(e.target.value)} className="fr-select fr-val">
          <option value="">Select…</option>
          {(selectedField.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input value={fieldValue} onChange={e => setFieldValue(e.target.value)}
          placeholder={isMulti ? 'val1, val2…' : 'value…'} className="fr-input" />
      )}
      <div className="fr-logic-toggle">
        <button type="button"
          className={`fr-logic-btn ${logicalOperator === 'AND' ? 'active' : ''}`}
          onClick={() => setLogicalOperator('AND')}
          title="AND — todos as condições devem ser verdadeiras">
          AND
        </button>
        <button type="button"
          className={`fr-logic-btn ${logicalOperator === 'OR' ? 'active' : ''}`}
          onClick={() => setLogicalOperator('OR')}
          title="OR — basta uma condição ser verdadeira">
          OR
        </button>
      </div>
      <button className="fr-btn-save" onClick={() => canSave && onSave({ field_name: fieldName, operator, field_value: fieldValue.trim(), logical_operator: logicalOperator })} disabled={!canSave}>✓</button>
      <button className="fr-btn-cancel" onClick={onCancel}>✕</button>
    </div>
  );
};

// ─── SubscriptionRow ─────────────────────────────────────────────────────────
const SubscriptionRow = ({ sub, onToggle, onUpdateEmail, onAddFilter, onEditFilter, onDeleteFilter, onToggleFilterMode }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [editingFilter, setEditingFilter] = useState(null); // filter_id | 'new' | null

  const Icon = (() => {
    const k = sub.icon ? (sub.icon.startsWith('Fi') ? sub.icon : `Fi${sub.icon}`) : 'FiBell';
    return FiIcons[k] || FiIcons.FiBell;
  })();

  const hasFilters = sub.filters?.length > 0;
  const isFiltered = sub.filter_mode === 'filtered';
  const typeName = t(`notification_type.${sub.notification_type_code}`, sub.type_name);
  const typeDesc = t(`notification_type.${sub.notification_type_code}_desc`, sub.description);

  const handleSaveFilter = async (data) => {
    if (editingFilter === 'new') await onAddFilter(sub.subscription_id, data);
    else await onEditFilter(editingFilter, data);
    setEditingFilter(null);
  };

  return (
    <div className={`nsub-row ${hasFilters ? 'nsub-has-filters' : ''} ${!sub.is_active ? 'nsub-inactive' : ''}`}>
      <div className="nsub-main">
        <div className="nsub-icon" style={{ color: sub.is_active ? sub.color : 'var(--gray-300)' }}><Icon size={16} /></div>
        <div className="nsub-info">
          <span className="nsub-name">{typeName}</span>
          <span className="nsub-desc">{typeDesc}</span>
        </div>
        {sub.entity_type && <span className="nsub-entity">{sub.entity_type.replace('_header','').replace('_',' ')}</span>}

        <div className="nsub-controls">
          {sub.email_enabled && (
            <label className="nsub-email-wrap" title={t('notifications.email_toggle','Email')}>
              <FiIcons.FiMail size={12} className={sub.notify_email ? 'email-on' : 'email-off'} />
              <input type="checkbox" checked={!!sub.notify_email} onChange={() => onUpdateEmail(sub)} />
              <span className="nsub-sm-slider" />
            </label>
          )}
          {sub.is_active && (
            <button
              className={`nsub-cond-btn ${expanded ? 'open' : ''} ${hasFilters ? 'cond-active' : ''}`}
              onClick={() => setExpanded(v => !v)}
              title={hasFilters ? `${sub.filters.length} condition(s)` : t('notifications.add_condition','Add conditions')}
            >
              <FiIcons.FiFilter size={12} />
              {hasFilters && <span className="nsub-cond-count">{sub.filters.length}</span>}
            </button>
          )}
          <label className="nsub-onoff">
            <input type="checkbox" checked={!!sub.is_active} onChange={() => onToggle(sub)} />
            <span className="nsub-onoff-slider" />
          </label>
        </div>
      </div>

      {expanded && sub.is_active && (
        <div className="nsub-cond-panel">
          <div className="nsub-cond-header">
            <span className="nsub-cond-title"><FiIcons.FiFilter size={11} /> {t('notifications.conditions','CONDITIONS')}</span>
            <div className="nsub-mode-toggle">
              <span>{t('notifications.notify_label','Notify:')}</span>
              <button className={`nsub-mode-btn ${!isFiltered ? 'active' : ''}`}
                onClick={() => isFiltered && onToggleFilterMode(sub.subscription_id, 'all')}>
                {t('notifications.always','Always')}
              </button>
              <button className={`nsub-mode-btn ${isFiltered ? 'active' : ''}`}
                onClick={() => !isFiltered && hasFilters && onToggleFilterMode(sub.subscription_id, 'filtered')}
                disabled={!hasFilters}>
                {t('notifications.when_match','When conditions match')}
              </button>
            </div>
          </div>

          <div className="nsub-filters">
            {!hasFilters && <span className="nsub-no-cond">{t('notifications.no_conditions','No conditions — fires for all events')}</span>}
            {sub.filters?.map(f => (
              editingFilter === f.filter_id
                ? <FilterRow key={f.filter_id} entityType={sub.entity_type || sub.notification_type_code} initial={f}
                    onSave={handleSaveFilter} onCancel={() => setEditingFilter(null)} />
                : (
                  <span key={f.filter_id} className="nsub-badge">
                    {sub.filters.indexOf(f) > 0 && (
                      <span className={`badge-logic badge-logic-${(f.logical_operator || 'AND').toLowerCase()}`}>
                        {f.logical_operator || 'AND'}
                      </span>
                    )}
                    <span className="badge-field">{f.field_name}</span>
                    <span className="badge-op">{t(`notification.filter.${f.operator}`, f.operator)}</span>
                    <span className="badge-val">{f.field_value}</span>
                    <button className="badge-edit" onClick={() => setEditingFilter(f.filter_id)} title="Edit">✎</button>
                    <button className="badge-del" onClick={() => onDeleteFilter(f.filter_id)} title="Remove">✕</button>
                  </span>
                )
            ))}
            {editingFilter === 'new' && (
              <FilterRow entityType={sub.entity_type || sub.notification_type_code} onSave={handleSaveFilter} onCancel={() => setEditingFilter(null)} />
            )}
          </div>

          {editingFilter !== 'new' && (
            <button className="nsub-add-cond" onClick={() => setEditingFilter('new')}>
              {t('notifications.add_condition','+ Add condition')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Shared list (used by both MySettings and AdminSettings) ─────────────────
export const SubscriptionList = ({ subscriptions, setSubscriptions, apiModule, toast_success, toast_error }) => {
  const { t } = useTranslation();
  const api = apiModule || notificationsAPI;

  const patch = (id, p) => setSubscriptions(prev => prev.map(s => s.subscription_id === id ? { ...s, ...p } : s));

  const handleToggle = async (sub) => {
    try {
      if (!sub.subscription_id && api.ensureSubscription) {
        // No row yet — create it via upsert
        const r = await api.ensureSubscription({
          notification_type_code: sub.notification_type_code,
          entity_type: sub.entity_type,
          is_active: true,
          notify_email: false,
          filter_mode: 'all',
        });
        if (!r.data.success) throw new Error(r.data.error || 'Failed');
        setSubscriptions(prev => prev.map(s =>
          s.notification_type_code === sub.notification_type_code && s.entity_type === sub.entity_type
            ? { ...s, ...r.data.data, is_subscribed: true, is_active: true, filters: [] }
            : s
        ));
        toast_success('Subscribed');
        return;   // ← prevent falling through to updateSubscription
      }
      await api.updateSubscription(sub.subscription_id, { is_active: !sub.is_active });
      patch(sub.subscription_id, { is_active: !sub.is_active });
      toast_success(sub.is_active ? 'Unsubscribed' : 'Subscribed');
    } catch (err) { toast_error(err.message || 'Failed'); }
  };
  const handleUpdateEmail = async (sub) => {
    try {
      if (!sub.subscription_id && api.ensureSubscription) {
        // No row yet — create it and enable email at the same time
        const r = await api.ensureSubscription({
          notification_type_code: sub.notification_type_code,
          entity_type: sub.entity_type,
          is_active: false,
          notify_email: true,
          filter_mode: 'all',
        });
        if (!r.data.success) throw new Error(r.data.error || 'Failed');
        setSubscriptions(prev => prev.map(s =>
          s.notification_type_code === sub.notification_type_code && s.entity_type === sub.entity_type
            ? { ...s, ...r.data.data, filters: [] }
            : s
        ));
        return;
      }
      await api.updateSubscription(sub.subscription_id, { notify_email: !sub.notify_email });
      patch(sub.subscription_id, { notify_email: !sub.notify_email });
    }
    catch { toast_error('Failed'); }
  };
  const handleToggleFilterMode = async (id, mode) => {
    try { await api.updateSubscription(id, { filter_mode: mode }); patch(id, { filter_mode: mode }); }
    catch { toast_error('Failed'); }
  };
  const handleAddFilter = async (subId, filter) => {
    try {
      const r = await api.addFilter(subId, filter);
      setSubscriptions(prev => prev.map(s => s.subscription_id === subId
        ? { ...s, filters: [...(s.filters || []), r.data.data], filter_mode: 'filtered' } : s));
      toast_success('Condition added');
    } catch { toast_error('Failed to add condition'); }
  };
  const handleEditFilter = async (filterId, filter) => {
    try {
      const r = await api.updateFilter(filterId, filter);
      setSubscriptions(prev => prev.map(s => ({
        ...s, filters: (s.filters || []).map(f => f.filter_id === filterId ? r.data.data : f),
      })));
      toast_success('Condition updated');
    } catch { toast_error('Failed to update condition'); }
  };
  const handleDeleteFilter = async (filterId) => {
    try {
      await api.deleteFilter(filterId);
      setSubscriptions(prev => prev.map(s => ({
        ...s,
        filters: (s.filters || []).filter(f => f.filter_id !== filterId),
        filter_mode: (s.filters || []).filter(f => f.filter_id !== filterId).length === 0 ? 'all' : s.filter_mode,
      })));
      toast_success('Condition removed');
    } catch { toast_error('Failed to remove condition'); }
  };

  const grouped = subscriptions.reduce((acc, s) => {
    const cat = s.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {});

  return (
    <>
      {Object.entries(grouped).map(([cat, subs]) => (
        <div key={cat} className="nsub-group">
          <div className="nsub-group-title">{t(`notification_category.${cat}`, cat)}</div>
          {subs.map(sub => (
            <SubscriptionRow key={`${sub.notification_type_code}-${sub.entity_type || 'null'}`} sub={sub}
              onToggle={handleToggle} onUpdateEmail={handleUpdateEmail}
              onToggleFilterMode={handleToggleFilterMode}
              onAddFilter={handleAddFilter} onEditFilter={handleEditFilter} onDeleteFilter={handleDeleteFilter}
            />
          ))}
        </div>
      ))}
    </>
  );
};

// ─── My Notification Settings ─────────────────────────────────────────────────
const NotificationSettings = () => {
  const { t } = useTranslation();
  const { toasts, removeToast, toast_success, toast_error } = useToast();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    notificationsAPI.getSubscriptionsWithFilters()
      .then(r => { if (r.data.success) setSubscriptions(r.data.data); })
      .catch(() => toast_error(t('messages.error_load','Failed to load')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    console.log('[NotificationSettings.jsx] mounted');
    return () => console.log('[NotificationSettings.jsx] unmounted');
  }, []);


  if (loading) return <div className="settings-page"><div className="loading">Loading…</div></div>;

  return (
    <div className="settings-page">
      <div className="page-header">
        <div>
          <h2>{t('notifications.settings_title','Notification Settings')}</h2>
          <p className="page-subtitle">{t('notifications.settings_subtitle','Manage notifications and when you receive them')}</p>
        </div>
      </div>
      <div className="nsub-list">
        <SubscriptionList subscriptions={subscriptions} setSubscriptions={setSubscriptions}
          toast_success={toast_success} toast_error={toast_error} />
      </div>
      <div className="toast-container">
        {toasts.map(toast => <Toast key={toast.id} {...toast} onClose={removeToast} />)}
      </div>
    </div>
  );
};

export default NotificationSettings;